import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _toUpper from 'lodash/toUpper';
// Serialization
import { deserialize } from 'serializr';
import { accessSchema } from './schemas';
// Color
import { accessResultPalette } from '../../components/core/colors';
// Permissions
import permissions from '../../constants/Permissions';
// Fragments
import { accessFragment } from './fragments';
// Models
import mEquipment from '../equipment';
// Custom
import { SEARCHFIELD_TYPES } from '../../constants/App';
import { findInList } from '../../utils/Tools';
import { dateFormatter } from '../../utils/DateUtils';

// Permissions used in model
const {
  pCpm: {
    customerRead, parkingRead, parkingCategoryRead, bookingRead,
  },
} = permissions;

/**
* Serialization
*/
const deserializeAccess = (data) => deserialize(accessSchema, data);

/**
* Lists
*/
const accessResultsList = [
  {
    key: 'success',
    value: 'success',
    labelId: 'access.result.success',
    icon: 'success',
    color: accessResultPalette.success,
  },
  {
    key: 'success_drop_off',
    value: 'success_drop_off',
    labelId: 'access.result.successDropOff',
    icon: 'success',
    color: accessResultPalette.success,
  },
  {
    key: 'errorExecution',
    value: 'error_execution',
    labelId: 'access.result.errorExecution',
    icon: 'warning',
    color: accessResultPalette.warning,
  },
  {
    key: 'errorDates',
    value: 'error_dates',
    labelId: 'access.result.errorDates',
    icon: 'forbid',
    color: accessResultPalette.error,
  },
  {
    key: 'errorPlanning',
    value: 'error_planning',
    labelId: 'access.result.errorPlanning',
    icon: 'forbid',
    color: accessResultPalette.error,
  },
  {
    key: 'errorCapacity',
    value: 'error_capacity',
    labelId: 'access.result.errorCapacity',
    icon: 'forbid',
    color: accessResultPalette.error,
  },
  {
    key: 'errorParkingCategoryAllotmentPlanning',
    value: 'error_parking_category_allotment_planning',
    labelId: 'access.result.errorParkingCategoryAllotmentPlanning',
    icon: 'forbid',
    color: accessResultPalette.error,
  },
  {
    key: 'errorDoors',
    value: 'error_doors',
    labelId: 'access.result.errorDoors',
    icon: 'forbid',
    color: accessResultPalette.error,
  },
  {
    key: 'errorAntipassback',
    value: 'error_antipassback',
    labelId: 'access.result.errorAntipassback',
    icon: 'forbid',
    color: accessResultPalette.error,
  },
  {
    key: 'errorPermanentExit',
    value: 'error_permanent_exit',
    labelId: 'access.result.errorPermanentExit',
    icon: 'forbid',
    color: accessResultPalette.error,
  },
  {
    key: 'errorForbiddenEntry',
    value: 'error_forbidden_entry',
    labelId: 'access.result.errorForbiddenEntry',
    icon: 'forbid',
    color: accessResultPalette.error,
  },
  {
    key: 'errorForbiddenExit',
    value: 'error_forbidden_exit',
    labelId: 'access.result.errorForbiddenExit',
    icon: 'forbid',
    color: accessResultPalette.error,
  },
  {
    key: 'errorIgnoredLicensePlate',
    value: 'error_ignored_license_plate',
    labelId: 'access.result.errorIgnoredLicensePlate',
    icon: 'forbid',
    color: accessResultPalette.error,
  },
  {
    key: 'errorForbiddenEntryDuringExitCourtesyDelay',
    value: 'error_forbidden_entry_during_exit_courtesy_delay',
    labelId: 'access.result.errorForbiddenEntryDuringExitCourtesyDelay',
    icon: 'forbid',
    color: accessResultPalette.error,
  },
  {
    key: 'errorUnknown',
    value: 'error_unknown',
    labelId: 'access.result.errorUnknown',
    icon: 'forbid',
    color: accessResultPalette.error,
  },
];

const accessTypesList = [
  {
    key: 'access',
    value: 'access',
    labelId: 'access.type.access',
    icon: 'accessTypeAccess',
  },
  {
    key: 'onDemand',
    value: 'on_demand',
    labelId: 'access.type.on_demand',
    icon: 'accessTypeOnDemand',
  },
  {
    key: 'triggerDoor',
    value: 'trigger_door',
    labelId: 'access.type.trigger_door',
    icon: 'accessTypeTriggerDoor',
  },
];

const accessUsagesList = [
  {
    key: 'input',
    value: 'input',
    labelId: 'access.usage.input',
    icon: 'accessUsageInput',
  },
  {
    key: 'output',
    value: 'output',
    labelId: 'access.usage.output',
    icon: 'accessUsageOutput',
  },
  {
    key: 'na',
    value: 'na',
    labelId: 'access.usage.na',
    icon: 'accessUsageNa',
  },
];

/**
* Getters
*/
const accessGetResultSuccess = accessResultsList[0];

const accessGetTypeAccess = accessTypesList[0];
const accessGetTypeOnDemand = accessTypesList[1];
const accessGetTypeTriggerDoor = accessTypesList[2];

const accessGetUsageInput = accessUsagesList[0];
const accessGetUsageOutput = accessUsagesList[1];

/**
* Utils
*/
const accessResult = (access) => findInList(accessResultsList, 'value', access, 'result');
const accessType = (access) => findInList(accessTypesList, 'value', access, 'type');
const accessUsage = (access) => findInList(accessUsagesList, 'value', access, 'usage');

const accessIsTriggerDoor = (access) => accessType(access) === accessGetTypeTriggerDoor;

const accessSource = (access) => {
  if (!access) {
    return null;
  }
  switch (access.type) {
    case 'access':
    case 'on_demand':
      if (!access.equipment) {
        return {
          source: access.source,
          icon: null,
        };
      }
      return {
        source: `${access.source} - ${access.equipment.name}`,
        icon: mEquipment.type(access.equipment).icon,
      };
    default:
      return {
        source: '',
        icon: findInList(accessTypesList, 'value', access, 'type').icon,
      };
  }
};

const accessActivityFilters = (type, date, timezone) => [
  {
    field: 'date',
    operator: SEARCHFIELD_TYPES.date.defaultOperator('after'),
    value: dateFormatter.toServerFormat(date, timezone, { hour: 0, minute: 0, second: 0 }),
  },
  {
    field: 'date',
    operator: SEARCHFIELD_TYPES.date.defaultOperator('before'),
    value: dateFormatter.toServerFormat(date, timezone, { hour: 23, minute: 59, second: 59 }),
  },
  {
    field: 'type',
    operator: SEARCHFIELD_TYPES.enum.defaultOperator(),
    value: _toUpper(accessGetTypeAccess.value),
  },
  {
    field: 'result',
    operator: SEARCHFIELD_TYPES.enum.defaultOperator(),
    value: _toUpper(accessGetResultSuccess.value),
  },
  {
    field: 'usage',
    operator: SEARCHFIELD_TYPES.enum.defaultOperator(),
    value: type === 'entries' ? _toUpper(accessGetUsageInput.value) : _toUpper(accessGetUsageOutput.value),
  },
];

/**
* GraphQL
*/
const queryPermissions = (hasPermission) => ({
  withParking: hasPermission({ needed: parkingRead, permissionsOrContext: 'carparkPermissions' }),
  withCustomer: hasPermission({ needed: customerRead, permissionsOrContext: 'carparkPermissions' }),
  withParkingCategory: hasPermission({ needed: parkingCategoryRead, permissionsOrContext: 'carparkPermissions' }),
  withBooking: hasPermission({ needed: bookingRead, permissionsOrContext: 'carparkPermissions' }),
});

const queListAccesses = (hasPermission) => `
  query ListAccesses($input: ListAccessesInput!, $pagination: PaginationInput, $search: ListAccessesSearchInput, $sort: [ListAccessesSortInput]) {
    listAccesses(input: $input, pagination: $pagination, search: $search, sort: $sort) {
      entries {
        ...accessFragment
      },
      pageNumber,
      pageSize,
      totalEntries,
      totalPages
    }
  }

  ${accessFragment(queryPermissions(hasPermission))}
`;

/**
* Model
*/
const access = {
  // Name and icon
  name: 'access',
  icon: 'access',

  // Serialization
  deserialize: deserializeAccess,

  // Lists
  resultsList: accessResultsList,
  typesList: accessTypesList,
  usagesList: accessUsagesList,

  // Utils
  result: accessResult,
  type: accessType,
  usage: accessUsage,
  isTriggerDoor: accessIsTriggerDoor,
  source: accessSource,
  activityFilters: accessActivityFilters,

  // List features
  listFeatures: {
    // Default list params
    defaultParams: {
      pagination: {
        page: 1,
        pageSize: 20,
      },
      sort: [
        { field: 'date', dir: 'desc' },
      ],
    },

    // Columns
    columnsQuantity: (hasPermission) => ({
      xs: 3,
      sm: 3,
      md: 3 + (hasPermission({ needed: customerRead, permissionsOrContext: 'carparkPermissions' }) | 0),
      lg: 5 + (hasPermission({ needed: customerRead, permissionsOrContext: 'carparkPermissions' }) | 0),
      xl: 5 + (hasPermission({ needed: customerRead, permissionsOrContext: 'carparkPermissions' }) | 0),
    }),
    columns: [
      {
        key: 'date',
        labelId: 'field.date',
        field: 'date',
        sortable: true,
      },
      {
        key: 'type',
        labelId: 'field.type',
        field: 'type',
        sortable: false,
        className: 'table-content-1 table-content-center',
      },
      {
        key: 'customer',
        permissions: {
          needed: customerRead,
          permissionsOrContext: 'carparkPermissions',
        },
        labelId: 'field.carparkUser',
        field: 'customer',
        sortable: false,
        className: 'hidden-sm-down',
      },
      {
        key: 'door',
        labelId: 'field.access',
        field: 'door',
        sortable: false,
        className: 'hidden-md-down',
      },
      {
        key: 'source',
        labelId: 'field.accessWay',
        field: 'source',
        sortable: false,
        className: 'hidden-md-down',
      },
      {
        key: 'result',
        labelId: 'field.result',
        field: 'result',
        sortable: false,
        className: 'hidden-sm-down',
      },
      {
        key: 'resultSmDown',
        labelId: 'field.result',
        field: 'result',
        sortable: false,
        className: 'hidden-md-up table-content-1',
      },
    ],

    // Search fields
    searchFields: [
      {
        key: 'begin',
        labelId: 'field.afterDate',
        field: 'begin',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'after',
        filter: {
          field: 'date',
        },
      },
      {
        key: 'end',
        labelId: 'field.beforeDate',
        field: 'end',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.date,
        scope: 'before',
        filter: {
          field: 'date',
        },
      },
      {
        key: 'type',
        labelId: 'field.type',
        field: 'type',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.enum,
        filter: {
          field: 'type',
        },
      },
      {
        key: 'parkingCategoryId',
        permissions: {
          needed: parkingCategoryRead,
          permissionsOrContext: 'carparkPermissions',
        },
        labelId: 'field.parkingCategory',
        field: 'parkingCategoryId',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'parkingCategoryId',
        },
      },
      {
        key: 'carparkCustomer',
        permissions: {
          needed: customerRead,
          permissionsOrContext: 'carparkPermissions',
        },
        labelId: 'field.carparkUser',
        field: 'carparkCustomer',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: true,
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'customerId',
          key: 'id',
        },
      },
      {
        key: 'carparkParking',
        permissions: {
          needed: `${parkingRead}_disabled`,
          permissionsOrContext: 'carparkPermissions',
        },
        labelId: 'field.parking',
        field: 'carparkParking',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: true,
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'parkingId',
          key: 'id',
        },
      },
      {
        key: 'doorId',
        labelId: 'field.access',
        field: 'doorId',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'doorId',
        },
      },
      {
        key: 'usage',
        labelId: 'field.usage',
        field: 'usage',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.enum,
        filter: {
          field: 'usage',
        },
      },
      {
        key: 'accessWay',
        labelId: 'field.accessWay',
        field: 'accessWay',
        initial: '',
        isUndefined: (value) => _isEmpty(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.string,
        withOperators: true,
        filter: {
          field: 'source',
        },
      },
      {
        key: 'equipmentId',
        labelId: 'field.equipment',
        field: 'equipmentId',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.resource,
        filter: {
          field: 'equipmentId',
        },
      },
      {
        key: 'result',
        labelId: 'field.result',
        field: 'result',
        initial: null,
        isUndefined: (value) => _isNil(value),
        needGetOnInit: false,
        type: SEARCHFIELD_TYPES.enum,
        filter: {
          field: 'result',
        },
      },
    ],
  },

  // Types
  accessGetTypeAccess,
  accessGetTypeOnDemand,
  accessGetTypeTriggerDoor,

  // GraphQL
  queListAccesses,
};

module.exports = access;



// WEBPACK FOOTER //
// ./models/cpm/access.js